import React, { useEffect, useState } from "react";
import { deleteField, doc, updateDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useUserContext } from "../../../context/UserProvider";
import { db } from "../../../firebase";
import { DeleteIcon, RunnerIcon } from "../../util/Icons";
import { useThemeContext } from "../../../context/ThemeProvider";
import { getActivityProvider, getProviderLabel } from "../../../utils/activityProviders";

export default function ActivityFeed({ activityFeed, selectedRace, getActivity, totalDistance }) {
  const { user } = useUserContext();
  const { colors } = useThemeContext();
  const [removedEntryIds, setRemovedEntryIds] = useState(() => new Set());
  const [deleteFeedback, setDeleteFeedback] = useState(null);
  const removeManualActivityFromChallengeCallable = httpsCallable(
    getFunctions(),
    "removeManualActivityFromChallenge"
  );

  useEffect(() => {
    setRemovedEntryIds(new Set());
    setDeleteFeedback(null);
  }, [selectedRace?.legId]);

  const visibleActivityFeed = [...activityFeed]
    .filter((entry) => !removedEntryIds.has(entry.key))
    .sort((a, b) => new Date(b?.value?.activity?.date).getTime() - new Date(a?.value?.activity?.date));

  async function deleteEntry(entry) {
    setDeleteFeedback(null);
    try {
      if (entry?.value?.provider === "manual" && (entry?.value?.origin || entry?.value?.recordedFrom)) {
        await removeManualActivityFromChallengeCallable({
          legId: selectedRace.legId,
          raceWriteEventId: entry.key,
        });
      } else {
        const newTotalDistance =
          totalDistance - parseFloat(entry.value.distance) > 0 ? parseFloat(totalDistance) - parseFloat(entry.value.distance) : 0;

        await updateDoc(doc(db, "races", selectedRace.legId, "subscribedUsers", user.uid), {
          [`times.${entry.key}`]: deleteField(),
          totalDistance: newTotalDistance,
        });
      }

      setRemovedEntryIds((currentIds) => new Set(currentIds).add(entry.key));
      setDeleteFeedback({ type: "success", message: "Activity deleted." });
      return true;
    } catch (err) {
      console.error("Unable to delete activity", err?.message || err);
      setDeleteFeedback({
        type: "error",
        message: err?.code === "functions/not-found"
          ? "This activity was already removed. Refreshing the activity list."
          : "We couldn't delete this activity. Please try again.",
      });
      await getActivity?.();
      return false;
    }
  }
  return (
    <div className="flex flex-col gap-2">
      <h3 className="my-4">Activity Feed</h3>
      {deleteFeedback && (
        <p
          className={`m-0 rounded-md px-3 py-2 text-sm ${
            deleteFeedback.type === "success" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
          }`}
          role={deleteFeedback.type === "error" ? "alert" : "status"}
        >
          {deleteFeedback.message}
        </p>
      )}
      {visibleActivityFeed.map((entry) => {
        let entryDate = new Date(entry.value.activity.date);
        // entryDate.setUTCHours(8)
        // const dateOptions = { year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" };
        const dateOptions = { year: "numeric", month: "short", day: "numeric"};
        const provider = getActivityProvider(entry);
        const providerLabel = getProviderLabel(provider);
        let color = "black";
        if (provider === "fitbit") {
          color = colors.fitbitBlueLight;
          entryDate.setUTCHours(12);
        }
        if (provider === "strava") color = colors.stravaOrange;
        if (provider === "garmin") color = colors.garminBlue;
        if (provider === "apple_health") color = colors.appleHealthPink;

        return (
          <div key={entry.key} className={`relative shadowBox gap-2 px-2 py-1 h-13 grid grid-cols-[25px,1fr,1fr,48px] items-center `}>
            <span
              title={providerLabel ? `Imported from ${providerLabel}` : undefined}
              aria-label={providerLabel ? `Imported from ${providerLabel}` : "Activity"}
            >
              <RunnerIcon color={color} />
            </span>
            <div className="">
              <p className="m-0 text-sm text-center">{entryDate.toLocaleDateString(undefined, dateOptions)}</p>
              {/* <p className="m-0">
                  {entry.value.activity.hour}:{entry.value.activity.minute} {entry.value.activity.amPm}
                </p> */}
            </div>
            <div className="flex flex-wrap gap-x-3">
              {/* <p className="m-0">Distance</p> */}
              <p className="m-0 text-sm text-right w-full">
                {parseFloat(entry.value.distance).toFixed(2)} {entry.value.distanceType}
              </p>
            </div>
            <DeleteButton entry={entry} callback={deleteEntry} />
          </div>
        );
      })}
    </div>
  );
}

export function DeleteButton({ entry, callback }) {
  const [showConfirm, setShowConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  async function confirmDelete() {
    setIsDeleting(true);
    await callback(entry);
    setIsDeleting(false);
    setShowConfirm(false);
  }

  return (
    <>
      {showConfirm ? (
        <div className="absolute right-2 z-20 flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-lg">
          <span className="whitespace-nowrap text-sm">Delete this activity?</span>
          <button
            type="button"
            disabled={isDeleting}
            className="rounded-md border border-slate-300 bg-white px-3 py-1 text-sm disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() => {
              setShowConfirm(false);
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={isDeleting}
            className="min-w-[76px] rounded-md bg-red-600 px-3 py-1 text-sm text-white hover:bg-red-700 disabled:cursor-wait disabled:opacity-70"
            onClick={confirmDelete}
          >
            {isDeleting ? "Deleting…" : "Delete"}
          </button>
        </div>
      ) : (
        <button
          type="button"
          aria-label="Delete activity"
          className="justify-self-end w-auto grid justify-center items-center cursor-pointer bg-red-300 active:bg-red-500 rounded-full"
          style={{boxShadow: "0px 0px 0px 2px rgb(252 165 165 / var(--tw-bg-opacity))"}}
          onClick={() => {
            setShowConfirm(true);
          }}
        >
          <DeleteIcon  />
        </button>
      )}
    </>
  );
}
